import authService from './authService';
import { Driver, DriverFormData, DriverSearchModel } from '../types/driver';
import { PaginatedResponse, ApiResponse } from '../types/common';

class DriverService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  async getAll(searchModel: DriverSearchModel): Promise<PaginatedResponse<Driver>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.is_active !== undefined && { is_active: searchModel.is_active.toString() }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/drivers?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar motoristas');

    const json = await response.json();
    return {
      success: true,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: json.data?.pagination?.page ?? json.pagination?.page ?? 1,
        limit: json.data?.pagination?.page_size ?? json.pagination?.limit ?? searchModel.pageSize,
        total: json.data?.pagination?.total_items ?? json.pagination?.total ?? 0,
        totalPages: json.data?.pagination?.total_pages ?? json.pagination?.totalPages ?? 1,
      },
    };
  }

  async getById(id: number): Promise<Driver> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/drivers/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Motorista não encontrado');
      throw new Error('Falha ao buscar motorista');
    }
    const data: ApiResponse<Driver> = await response.json();
    return data.data!;
  }

  async getByEmail(email: string): Promise<Driver | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/drivers/by-email?email=${encodeURIComponent(email)}`);
    if (!response.ok) return null;
    const data: ApiResponse<Driver> = await response.json();
    return data.data ?? null;
  }

  async create(formData: DriverFormData): Promise<Driver> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/drivers', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao criar motorista');
    }
    const data: ApiResponse<Driver> = await response.json();
    return data.data!;
  }

  async update(id: number, formData: Partial<DriverFormData>): Promise<Driver> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/drivers/${id}`, formData);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Motorista não encontrado');
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao atualizar motorista');
    }
    const data: ApiResponse<Driver> = await response.json();
    return data.data!;
  }

  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/drivers/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Motorista não encontrado');
      throw new Error('Falha ao excluir motorista');
    }
  }
}

export const driverService = new DriverService();
export default driverService;
